import {Phone, Calendar, Shield, Check, Zap, MapPin, Car, Award} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const TeslaModelYPpfPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does Tesla Model Y PPF cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front coverage for Model Y starts at $1,299, full front coverage starts at $1,899, and full vehicle protection starts at $4,499. Final pricing depends on paint condition and specific coverage areas. Larger vehicles and specialty films may cost more."
          }
        },
        {
          "@type": "Question",
          "name": "How long does Model Y PPF installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front takes 1 day, full front requires 2 days, and full vehicle PPF takes 3-5 days. We never rush installation to ensure flawless results."
          }
        },
        {
          "@type": "Question",
          "name": "Should I get PPF on my new Model Y immediately?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Applying PPF within the first few weeks protects factory paint from day one. Many San Diego Model Y owners protect their vehicle before the first highway drive to prevent rock chips."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Tesla Model Y PPF San Diego | Paint Protection Film Installation"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional Tesla Model Y PPF installation in San Diego. Protect your Model Y with premium paint protection film. Self-healing technology, manufacturer-backed film warranties. Serving Vista, Carlsbad, Oceanside.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/tesla-model-y-ppf-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Tesla Model Y PPF San Diego - Professional Paint Protection Film Installation</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your Tesla Model Y with professional paint protection film installation in San Diego. Our installers specialize in Model Y-specific coverage, delivering careful protection against rock chips, scratches, and environmental damage.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              The Model Y's compact SUV design and lower ground clearance make rocker panels and front bumpers especially vulnerable to road debris. We use precision-cut PPF templates designed specifically for Model Y's unique body lines, ensuring complete protection without cutting on your paint.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Model Y PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Model Y Needs PPF */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Tesla Model Y Owners Choose PPF in San Diego</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              The Tesla Model Y is San Diego's most popular electric SUV, combining family practicality with Tesla's cutting-edge technology. However, Model Y's water-based paint and lower ride height make it particularly susceptible to road debris, rock chips, and parking lot damage. <a href="/paint-protection-film" className="text-cyan-400 hover:underline">Paint protection film</a> provides invisible armor that maintains your Model Y's pristine appearance and resale value.
            </p>
            <p>
              Model Y owners in Vista, Carlsbad, Oceanside, and throughout North County San Diego face unique challenges: frequent highway driving on I-5 and I-15, coastal salt air, intense UV exposure, and off-road adventures to local beaches and trails. PPF shields against all these threats while maintaining the sleek factory finish that makes the Model Y so attractive.
            </p>
            <p>
              Our Model Y PPF installations use bulk film or precision-cut patterns selected for this vehicle's dimensions and body style. We protect critical areas including the low front bumper, rocker panels (vulnerable due to reduced ground clearance), rear bumper loading area, door edges, mirrors, and A-pillars. Many families protect their Model Y immediately after delivery to preserve factory paint from the first mile.
            </p>
            <p>
              Combining <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF</a> with <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> creates a fully protected, customized Model Y. We offer package pricing for combined services, completing your entire vehicle protection and customization in one visit to our Vista facility.
            </p>
          </div>
        </div>
      </section>

      {/* Model Y Coverage Options & Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model Y PPF Coverage & Pricing</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Essential Model Y coverage for daily driving: front bumper, partial hood (12-18"), mirrors, and headlights. Protects the highest-impact zones from highway debris and rock chips.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complete front bumper coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Side mirrors and headlights</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Self-healing top coat</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Comprehensive Model Y front-end shield: full hood, bumper, fenders, mirrors, headlights, and fog lights. Maximum highway protection for frequent commuters and road trip families.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All partial coverage included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Rocker panel add-on available</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>10-year manufacturer warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Protection</h3>
              <p className="text-gray-300 mb-6">
                Complete Model Y protection: every painted surface wrapped in self-healing PPF. Ultimate investment protection for new vehicles and maximum resale value preservation.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rocker panels, quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper, liftgate</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Permanent paint preservation</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            All packages include professional paint preparation, precision installation, and manufacturer warranty. Starting prices shown are for Tesla Model Y. Exact pricing will be determined after a free consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Model Y Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model Y-Specific Protection Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Rocker Panel Armor</h3>
              <p className="text-gray-300">Model Y's lower ride height exposes rockers to constant road debris. PPF prevents chipping and maintains clean side profile appearance.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Liftgate Protection</h3>
              <p className="text-gray-300">Rear hatch and bumper areas receive frequent cargo loading contact. PPF shields against scratches from groceries, luggage, and outdoor gear.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Technology</h3>
              <p className="text-gray-300">Minor scratches and swirl marks disappear with heat exposure from the sun or warm water, maintaining showroom appearance long-term.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Resale Value Protection</h3>
              <p className="text-gray-300">Model Y with protected paint commands premium resale prices. PPF-protected vehicles show no wear, dramatically increasing buyer confidence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model Y PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Model Y Consultation</h3>
                <p className="text-gray-300">We inspect your Model Y's current paint condition, discuss your driving patterns (highway commuting, beach trips, off-road use), and recommend coverage options that match your protection goals and budget. You'll receive a transparent quote with no hidden fees.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Decontamination & Correction</h3>
                <p className="text-gray-300">Your Model Y receives a thorough wash, clay bar treatment to remove embedded contaminants, and paint correction if needed. For new vehicles, we remove delivery swirl marks and transport scratches. Used Model Y vehicles receive paint polishing to restore a perfect finish before PPF application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision PPF Installation</h3>
                <p className="text-gray-300">Using Model Y-specific bulk or precision-pattern methods, we apply premium self-healing PPF with professional wet installation techniques. All edges are wrapped or tucked for invisible protection. We properly protect cameras, sensors, charging port, and Autopilot hardware during installation.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Control & Delivery</h3>
                <p className="text-gray-300">Multi-point inspection ensures perfect edges, bubble-free application, and complete adhesion. We verify all sensors and cameras function properly, provide care instructions, and deliver warranty documentation before your Model Y leaves our Vista facility.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model Y Protection Zones */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Critical Model Y Protection Zones</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Front Bumper & Hood</h3>
              <p className="text-gray-300 mb-4">Model Y's sloped front design channels rock chips directly onto the bumper and hood leading edge. These areas receive 80% of all paint damage from highway driving and freeway debris.</p>
              <p className="text-gray-300">Full front bumper coverage with wrapped edges and full or partial hood protection ensures your Model Y's front end remains flawless despite daily San Diego commutes on I-5, I-15, and SR-78.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Rocker Panels & Fenders</h3>
              <p className="text-gray-300 mb-4">Lower ground clearance makes Model Y rockers vulnerable to road spray, gravel, and parking lot curbs. Front fenders behind the wheels take constant abuse from tire-thrown debris.</p>
              <p className="text-gray-300">PPF coverage on rockers and fenders prevents unsightly chipping and maintains clean side profile lines that define the Model Y's sporty SUV aesthetic.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Rear Hatch & Loading Area</h3>
              <p className="text-gray-300 mb-4">Families loading groceries, sports equipment, camping gear, and beach supplies constantly contact the rear bumper and liftgate sill. Without protection, this area develops scratches within months.</p>
              <p className="text-gray-300">Rear bumper and loading edge PPF creates an invisible scratch-proof barrier, keeping your Model Y's cargo area pristine through years of active family use.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Door Edges & Handles</h3>
              <p className="text-gray-300 mb-4">Model Y's flush door handles and painted door edges chip easily from rings, keys, and contact with adjacent vehicles in tight San Diego parking lots and garages.</p>
              <p className="text-gray-300">Door edge and handle PPF prevents costly paint touch-ups and maintains factory appearance in high-contact areas that traditional protection overlooks.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model Y PPF FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does Tesla Model Y PPF cost in San Diego?</h3>
              <p className="text-gray-300">Partial front coverage for Model Y starts at $1,299, full front coverage starts at $1,899, and full vehicle protection starts at $4,499. Final pricing depends on your Model Y's paint condition, chosen coverage areas, and any additional services like paint correction — larger vehicles and specialty films may cost more. We provide detailed quotes after a free inspection.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does Model Y PPF installation take?</h3>
              <p className="text-gray-300">Partial front takes 1 day, full front requires 2 days, and full vehicle PPF takes 3-5 days depending on paint preparation needs. We never rush installation—proper application and curing time ensure bubble-free results and long-lasting adhesion that protects your Model Y for years.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Should I get PPF on my new Model Y immediately?</h3>
              <p className="text-gray-300">Absolutely. Applying PPF within the first few weeks protects factory paint from day one. Many San Diego Model Y owners schedule installation before their first highway drive to prevent rock chips during break-in miles. Early protection = maximum resale value.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does PPF interfere with Tesla sensors or Autopilot?</h3>
              <p className="text-gray-300">No. We carefully mask all cameras, sensors, and Autopilot hardware during installation. PPF is optically clear and doesn't affect radar, ultrasonic sensors, or camera-based features. Your Model Y's Full Self-Driving capability and safety systems function perfectly through the film.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I combine Model Y PPF with chrome delete or window tint?</h3>
              <p className="text-gray-300">Yes! Most Model Y owners combine <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF</a>, <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a>, and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> for complete protection and blacked-out aesthetic. We offer package discounts for combined services completed in one visit.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What warranty comes with Model Y PPF?</h3>
              <p className="text-gray-300">Premium PPF includes a 10-year manufacturer warranty against yellowing, cracking, peeling, and delamination. Our installation workmanship is also guaranteed—we repair any bubbling, lifting, or edge issues at no charge throughout the warranty period.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Tesla Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/tesla-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Professional PPF for all Tesla models: Model S, 3, X, Y, and Cybertruck</p>
            </a>
            <a href="/paint-protection-film-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Premium PPF installation for all vehicle makes and models throughout San Diego County</p>
            </a>
            <a href="/chrome-delete-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete San Diego</h3>
              <p className="text-gray-400 text-sm">Blacked-out trim and window surrounds for sleek Model Y customization</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Serving Tesla Model Y Owners Throughout San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in Vista, California, we provide professional Model Y PPF installation to owners in North County San Diego, coastal communities, and greater San Diego metro area.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-center">
            <div>
              <p className="font-semibold text-white mb-2">North County</p>
              <p className="text-sm"><a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> • <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a> • <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a> • <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a> • San Marcos • Escondido</p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">Coastal</p>
              <p className="text-sm"><a href="/ppf-del-mar" className="text-cyan-400 hover:underline">Del Mar</a> • <a href="/ppf-solana-beach" className="text-cyan-400 hover:underline">Solana Beach</a> • <a href="/ppf-la-jolla" className="text-cyan-400 hover:underline">La Jolla</a> • <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">East County</p>
              <p className="text-sm"><a href="/ppf-poway" className="text-cyan-400 hover:underline">Poway</a> • Ramona • Santee • El Cajon</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Tesla Model Y Today</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free Model Y PPF consultation and receive a custom quote. Same-week appointments available for San Diego County Tesla owners.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Model Y PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default TeslaModelYPpfPage
